/**
 * Single radio option showing a license's seats and expiry, used by
 * SeatAssignmentModal and ReactivateModal.
 *
 * Extracted from UserManagementPage.tsx (Sub-PR 6.3).
 */

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24
const MS_PER_DAY = MS_PER_SECOND * SECONDS_PER_MINUTE * MINUTES_PER_HOUR * HOURS_PER_DAY
const EXPIRY_RED_DAYS = 14
const EXPIRY_AMBER_DAYS = 30

interface LicenseOptionData {
  id: string
  licenseKey: string
  seatsAvailable: number
  seatsTotal: number
  validUntil: string | null | undefined
}

interface LicenseOptionRadioProps {
  option: LicenseOptionData
  groupName: string
  selectedId: string
  onSelect: (id: string) => void
}

export function LicenseOptionRadio({
  option,
  groupName,
  selectedId,
  onSelect,
}: LicenseOptionRadioProps): React.ReactElement {
  const daysUntilExpiry = option.validUntil
    ? Math.ceil((new Date(option.validUntil).getTime() - Date.now()) / MS_PER_DAY)
    : null
  const expiryClass = computeExpiryClass(daysUntilExpiry)
  const expiryLabel = computeExpiryLabel(daysUntilExpiry)
  const isSelected = selectedId === option.id

  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
        isSelected
          ? 'border-[#4e5e82] bg-[#4e5e82]/5 dark:bg-[#8a9bba]/10'
          : 'border-stone-200 hover:border-stone-300 dark:border-stone-700 dark:hover:border-stone-600'
      }`}
    >
      <input
        type="radio"
        name={groupName}
        value={option.id}
        checked={isSelected}
        onChange={(e) => {
          onSelect(e.target.value)
        }}
        className="mt-1 h-4 w-4 text-[#4e5e82] focus:ring-[#4e5e82]"
      />
      <div className="min-w-0 flex-1">
        <div className="font-mono text-sm text-stone-900 dark:text-stone-100">
          {option.licenseKey}
        </div>
        <div className="mt-1 flex items-center gap-4 text-xs">
          <span className="text-stone-500 dark:text-stone-400">
            {option.seatsAvailable} / {option.seatsTotal} available
          </span>
          <span className={expiryClass}>{expiryLabel}</span>
        </div>
        {option.validUntil && (
          <div className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
            Valid until {new Date(option.validUntil).toLocaleDateString()}
          </div>
        )}
      </div>
    </label>
  )
}

function computeExpiryClass(daysUntilExpiry: number | null): string {
  if (daysUntilExpiry === null) return 'text-blue-600 dark:text-blue-400'
  if (daysUntilExpiry < EXPIRY_RED_DAYS) return 'text-red-600 dark:text-red-400'
  if (daysUntilExpiry < EXPIRY_AMBER_DAYS) return 'text-amber-600 dark:text-amber-400'
  return 'text-stone-500 dark:text-stone-400'
}

function computeExpiryLabel(daysUntilExpiry: number | null): string {
  if (daysUntilExpiry === null) return 'Perpetual'
  if (daysUntilExpiry < 0) return `Expired ${String(Math.abs(daysUntilExpiry))} days ago`
  return `Expires in ${String(daysUntilExpiry)} days`
}
